import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { Select, Input, Alert, Button, Tag, Icon, Modal, Popover, Tooltip, FormModel } from "ant-design-vue";
import { fmtMsg, MessageHelper, NotificationType } from "commonui";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { getModule } from "vuex-module-decorators";
import PageTitle from "@/components/page-title/page-title.vue";
import { VersionGroupModule } from "@/modules/versiongroup";
import TitleListState from "@/modules/title-list";
import { VCSLevelModel } from "@/models/title-list-model";
import { AssetListLocale, CommonLocale } from "@/locales/localeid";
import { disabledDueToAccessLevel, getRouteAccessLevel, isAnyVersionSelected } from "@/util/func";
import { Drawer } from "@/components/drawer";
import {
	AssetListGridModel,
	AssetListResponseModel,
	AssetListService,
	AsssetAddEditModel,
	SaveAssetRequestModel,
	SaveAssetTagsRequestModel
} from "@/services/asset-list";
import { CurriculumType, TagType } from "@/util/enum";
import { orderBy } from "lodash";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import { AssetListValidations, NoneVersionGroup } from "@/util/constants";
import { AssetTagResponseModel, TagsService } from "@/services/tag";
import { ImageMetaData } from "@/models/image-meta-data";
import ContentImageEditor from "@/components/content-image-editor/content-image-editor.vue";
import { IImageSelectorType } from "@/components/content-image-editor/content-image-editor";
import IContentImageEditor from "@/components/content-image-editor/content-image-editor";

@Component({
	components: {
		PageTitle,
		Grid,
		Select,
		Option: Select.Option,
		Input,
		Alert,
		Button,
		Tag,
		Icon,
		Modal,
		Popover,
		Tooltip,
		Drawer,
		Form: FormModel,
		FormItem: FormModel.Item,
		ContentImageEditor
	}
})
export default class AssetList extends Vue {
	@Ref() readonly assetGridRef!: GridRef;
	public titleListModule = getModule(TitleListState);
	private assetListService = AssetListService;
	private tagsService = TagsService;
	public versionGroupModule = VersionGroupModule;

	public vcsLevels: VCSLevelModel[] = this.titleListModule.levels;
	public searchText: string = "";
	public filterUnitId: number = -1;
	public allUnitId: number = -1;
	public noVersion: boolean = false;
	public assetsLoading: boolean = false;
	public assetsList: AssetListResponseModel[] = [];
	public assetListForGrid: AssetListGridModel[] = [];
	public selectedAssetTags: AssetTagResponseModel[] | null = null;
	public isMoreClicked: boolean = false;
	public isBothVGSelected: boolean = false;
	public currentPage: number = 1;
	public isAddAsset: boolean = false;
	public isEditAsset: boolean = false;
	public tagsList: AssetTagResponseModel[] = [];
	public selectedTags: AssetTagResponseModel[] = [];
	public unselectedTagsId: string[] = [];
	public addEditSaveBtnLoading: boolean = false;
	public addEditSaveBtnDisabled: boolean = false;
	public assetDeleteBtnLoading: boolean = false;
	public addEditDeleteBtnDisabled: boolean = false;
	public isTagNameError: boolean = false;
	public assetsSaveBtnLoading: boolean = false;
	public isTagNameUniqueError: boolean = false;
	public tagsListNames: string[] = [];
	public selectedTagsName: string[] = [];
	public isDescriptionLengthError: boolean = false;


	public addEditForm: AsssetAddEditModel = {
		id: 0,
		description: "",
		pageId: null,
		mediaId: null,
		metadata: "",
		target: 0,
		references: 0,
		tagIds: [],
		unitId: 0,
		component: "",
		contentId: "",
		unitName: ""
	};

	$refs!: {
		imageEditor: IContentImageEditor;
	};

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		} else {
			if (
				this.versionGroupModule.selectedGsVersion !== NoneVersionGroup &&
				this.versionGroupModule.selectedLsVersion !== NoneVersionGroup
			) {
				this.isBothVGSelected = true;
			}
		}
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
		if (this.assetGridRef) {
			this.assetGridRef.setCurrentPage(1);
		}
		this.fetchTagsList();
	}

	@Watch("versionGroupModule.selectedGsVersion", { immediate: true })
	@Watch("versionGroupModule.selectedLsVersion", { immediate: true })
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
			this.filterUnitId = -1;

			if (
				this.versionGroupModule.selectedGsVersion !== NoneVersionGroup &&
				this.versionGroupModule.selectedLsVersion !== NoneVersionGroup
			) {
				this.vcsLevels = this.titleListModule.levels;
				this.isBothVGSelected = true;
			} else if (
				this.versionGroupModule.selectedGsVersion !== NoneVersionGroup &&
				this.versionGroupModule.selectedLsVersion === NoneVersionGroup
			) {
				this.vcsLevels = this.titleListModule.levels.filter(
					(vcsLevel) => vcsLevel.curriculumType === CurriculumType.GrapeSEED
				);
				this.isBothVGSelected = false;
			} else if (
				this.versionGroupModule.selectedGsVersion === NoneVersionGroup &&
				this.versionGroupModule.selectedLsVersion !== NoneVersionGroup
			) {
				this.vcsLevels = this.titleListModule.levels.filter(
					(vcsLevel) => vcsLevel.curriculumType === CurriculumType.LittleSEED
				);
				this.isBothVGSelected = false;
			}

			if (this.assetGridRef) {
				this.assetGridRef.reload();
				this.assetGridRef.setCurrentPage(1);
			}
		} else {
			this.noVersion = true;
		}
	}

	@Watch("addEditForm.pageId")
	enableOrDisableAddEditSaveBtn() {
		if (this.addEditForm.pageId && this.addEditForm.description) {
			this.addEditSaveBtnDisabled = true;
		} else {
			this.addEditSaveBtnDisabled = false;
			this.unitOrComponentChange({ unitId: -1, mediaId: "", componentName: "" });
		}
	}

	validateAddEditForm(item: string, isPassed: boolean, error: string) {
		if (isPassed != this.addEditSaveBtnDisabled) this.addEditSaveBtnDisabled = isPassed;
	}

	public assetAddEditFormRules = {
		description: [
			{
				required: true,
				message: fmtMsg(this.assetListLocale.AssetListDrawerDescriptionRequiredText),
				trigger: "change"
			},
			{ validator: this.assetDescriptionValidator, trigger: "change" }
		]
	};

	assetDescriptionValidator(rule: any, value: string, callback: (msg?: string) => void) {

		if (value && value.trim().length > AssetListValidations.assetDescriptionCharacterLimit) {
			callback(fmtMsg(this.assetListLocale.AssetListDrawerDescriptionLengthErrorText, {
				0: AssetListValidations.assetDescriptionCharacterLimit
			}));
			this.addEditSaveBtnDisabled = false;
			this.isDescriptionLengthError = true;
			return;
		} else {
			this.isDescriptionLengthError = false;
		}

		if (!value.trim().length) {
			callback(fmtMsg(this.assetListLocale.AssetListDrawerDescriptionRequiredText));
			this.addEditSaveBtnDisabled = false;
			return;
		}

		if (value.trim().length && !this.addEditForm.pageId) {
			callback();
			this.addEditSaveBtnDisabled = false;
			return;
		}

		callback();
	}

	get selectedGSVGId() {
		return this.versionGroupModule.selectedGsVersion;
	}

	get assetListLocale(): any {
		return AssetListLocale;
	}

	get commonLocale(): any {
		return CommonLocale;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	fetchTagsList() {
		this.tagsService.getTagsList().then((data) => {
			this.tagsList = data;
			this.tagsListNames = data.map((tag) => tag.name);
		});
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	getAssetListParams() {
		return {
			searchText: this.searchText,
			gsVersionGroupId:
				this.versionGroupModule.selectedGsVersion !== NoneVersionGroup
					? this.versionGroupModule.selectedGsVersion
					: null,
			lsVersionGroupId:
				this.versionGroupModule.selectedLsVersion !== NoneVersionGroup
					? this.versionGroupModule.selectedLsVersion
					: null,
			unitId: this.filterUnitId === this.allUnitId ? null : this.filterUnitId
		};
	}

	get assetListServiceFunc() {
		return this.assetListService.getAssetList;
	}

	formatData(data) {
		let allLevels = orderBy(this.titleListModule.levels, ["curriculumType", "Unit"], ["desc", "asc"]);
		allLevels = allLevels.map((level, index) => {
			return {
				...level,
				sequence: index
			};
		});

		if (!data.data.length) {
			//If we are on the first page so we don't need to go back to previous page 
			if (this.currentPage > 1) {
				this.currentPage = this.currentPage - 1;
				this.assetGridRef.setCurrentPage(this.currentPage);
				this.assetGridRef.reload();
			}
		}

		return data.data.map((assetData) => {
			return {
				...assetData,
				unitName: this.vcsLevels.find((level) => level.id === assetData.unitId)?.displayName,
				sequence:
					allLevels.find((level) => level.id == assetData.unitId)?.sequence ||
					allLevels.findIndex((level) => level.id == assetData.unitId),
				tagsJoined: assetData.tags ? assetData.tags.map((tag) => tag ? tag.name : "").join(", ") : ""
			};
		});
	}

	displayTagsPopover(tags) {
		this.selectedAssetTags = tags && tags.length ? (tags.length > 2 ? tags.slice(2) : tags) : null;
		this.selectedTags = this.selectedAssetTags ? this.selectedAssetTags : [];
	}

	onFilterUnitChange() {
		if (this.assetGridRef) {
			this.assetGridRef.reload();
			this.assetGridRef.setCurrentPage(1);
		}
	}

	fetchAssetList() {
		if (this.assetGridRef) {
			this.assetGridRef.reload();
			this.assetGridRef.setCurrentPage(1);
		}
	}

	handlePaginationChange(currentPage: number) {
		this.currentPage = currentPage;
	}

	get persistedCurrentPage() {
		return this.currentPage;
	}

	get drawerTitle() {
		if (this.isAddAsset) {
			return fmtMsg(this.assetListLocale.AssetListDrawerAddAssetText);
		} else if (this.isEditAsset) {
			return fmtMsg(this.assetListLocale.AssetListDrawerEditAssetText);
		} else {
			return "";
		}
	}

	onAddAsset() {
		this.isAddAsset = true;
	}

	handleTagsNameSelection(values: string[]) {
		const tagIdsOfSelectedValues = this.tagsList
			.filter((tag) => values.includes(tag.name.toLowerCase()))
			.map((tag) => tag.id.toString());

		this.addEditForm.tagIds = tagIdsOfSelectedValues;

		const allTagNames = this.tagsList.map((tag) => tag.name);
		const tagNotPresent = values.find((value) => !allTagNames.includes(value));

		if (tagNotPresent) {
			this.addEditSaveBtnDisabled = false;
			this.addEditDeleteBtnDisabled = true;

			const isTagNameDuplicate = this.tagsList.some(
				(tag) => tag.name.toLowerCase() === tagNotPresent.toLowerCase()
			);
			if (isTagNameDuplicate) {
				this.isTagNameUniqueError = true;
				return;
			}
			if (tagNotPresent.length > AssetListValidations.tagNameCharacterLimit) {
				this.isTagNameError = true;
				return;
			} else {
				this.isTagNameError = false;
				this.tagsService
					.saveTag({
						name: tagNotPresent.toLowerCase(),
						tagTypeId: TagType.Asset
					})
					.then((data) => {
						this.fetchTagsList();

						this.addEditForm.tagIds.push(data.id.toString());

						if (this.addEditForm.pageId && this.addEditForm.description) {
							this.addEditSaveBtnDisabled = true;
							this.addEditDeleteBtnDisabled = false;
						} else {
							this.addEditSaveBtnDisabled = false;
							this.addEditDeleteBtnDisabled = true;
						}
					})
					.catch(() => {
						this.selectedTagsName = this.selectedTagsName.filter((tagName) => tagName !== tagNotPresent);
					});
			}
		} else {
			this.isTagNameError = false;
			this.isTagNameUniqueError = false;
			if (this.addEditForm.pageId && this.addEditForm.description) {
				this.addEditSaveBtnDisabled = true;
				this.addEditDeleteBtnDisabled = false;
			} else {
				this.addEditSaveBtnDisabled = false;
				this.addEditDeleteBtnDisabled = true;
			}
		}
	}

	onAssetTagRemove(asset, tagId: number) {
		this.unselectedTagsId = asset.tags.map((tag) => tag.id).filter((id) => id != tagId.toString());
		const params: SaveAssetTagsRequestModel = {
			tagIds: this.unselectedTagsId.map((id) => Number(id))
		};

		this.assetsSaveBtnLoading = true;
		this.assetListService
			.saveAssetTags(params, asset.id)
			.then(() => {
				this.selectedAssetTags = this.selectedAssetTags?.length
					? this.selectedAssetTags?.filter((tag) => tag.id != tagId)
					: [];
				this.assetsSaveBtnLoading = false;
				this.assetGridRef.reload();
			})
			.catch(() => {
				this.assetsSaveBtnLoading = false;
			});
	}

	onAddEditDrawerClose() {
		this.isAddAsset = false;
		this.isEditAsset = false;
		this.addEditSaveBtnLoading = false;
		this.assetDeleteBtnLoading = false;
		this.addEditSaveBtnDisabled = true;
		this.selectedTagsName = [];
		this.addEditForm = {
			id: 0,
			description: "",
			pageId: null,
			mediaId: null,
			metadata: "",
			target: 0,
			references: 0,
			tagIds: [],
			unitId: 0,
			component: "",
			contentId: "",
			unitName: ""
		};
	}

	onAddEditSave() {
		this.addEditSaveBtnLoading = true;

		const params: SaveAssetRequestModel = {
			description: this.addEditForm.description,
			mediaId: this.addEditForm.mediaId,
			pageId: this.addEditForm.pageId,
			tagIds: this.addEditForm.tagIds.map((id) => Number(id)),
			targetCount: this.addEditForm.target,
			metadata: this.addEditForm.metadata
		};

		if (this.isAddAsset) {
			this.assetListService
				.saveAsset(params)
				.then(() => {
					this.addEditSaveBtnLoading = false;
					MessageHelper.Message(
						NotificationType.Success,
						fmtMsg(this.assetListLocale.AssetListAssetSaveSuccessText)
					);
					this.onAddEditDrawerClose();
					this.assetGridRef.reload();
				})
				.catch(() => {
					this.addEditSaveBtnLoading = false;
					MessageHelper.Message(
						NotificationType.Failed,
						fmtMsg(this.assetListLocale.AssetListAssetSaveFailedText)
					);
				});
		} else if (this.isEditAsset) {
			this.assetListService
				.editAsset(params, this.addEditForm.id)
				.then(() => {
					this.addEditSaveBtnLoading = false;
					MessageHelper.Message(
						NotificationType.Success,
						fmtMsg(this.assetListLocale.AssetListAssetSaveSuccessText)
					);
					this.onAddEditDrawerClose();
					this.assetGridRef.reload();
				})
				.catch(() => {
					this.addEditSaveBtnLoading = false;
					MessageHelper.Message(
						NotificationType.Failed,
						fmtMsg(this.assetListLocale.AssetListAssetSaveFailedText)
					);
				});
		}
	}

	onAssetDelete() {
		const onOk = async () => {
			this.assetDeleteBtnLoading = true;
			this.assetListService
				.deleteAsset(this.addEditForm.id)
				.then(() => {
					MessageHelper.Message(
						NotificationType.Success,
						fmtMsg(this.assetListLocale.AssetListAssetDeleteSuccessText)
					);
					this.onAddEditDrawerClose();
					this.assetGridRef.reload();
				})
				.catch(() => {
					MessageHelper.Message(
						NotificationType.Failed,
						fmtMsg(this.assetListLocale.AssetListAssetDeleteFailedText)
					);
					this.onAddEditDrawerClose();
				});
		};

		const onCancel = () => {
			return;
		};

		Modal.confirm({
			okText: fmtMsg(this.assetListLocale.AssetListDrawerDeleteAssetOkText),
			title: fmtMsg(this.assetListLocale.AssetListDrawerDeleteAssetTitle),
			cancelText: fmtMsg(this.assetListLocale.AssetListDrawerDeleteAssetCancelText),
			onOk,
			onCancel
		});
	}

	onRemoveImage() {
		this.resetImageData();
	}

	private resetImageData() {
		this.addEditForm.pageId = "";
		this.addEditForm.metadata = "";
		this.addEditForm.component = "";
		this.addEditForm.contentId = "";
		this.addEditForm.unitId = 0;
		this.addEditForm.unitName = "";
	}

	onImageEditCancel() {
		if (!this.addEditForm.pageId || this.addEditForm.pageId === "") {
			this.resetImageData();
		}
	}

	onEditAsset(assetId: number) {
		this.isAddAsset = false;
		this.assetListService.getAsset(assetId).then((asset) => {
			this.addEditForm = {
				id: asset.id,
				description: asset.description,
				unitId: asset.unitId,
				target: asset.targetCount,
				references: asset.references,
				tagIds: asset.tags.map((tag) => tag.id.toString()),
				pageId: asset.pageId,
				mediaId: asset.mediaId,
				metadata: asset.metaData,
				component: asset.component,
				contentId: asset.contentId,
				unitName: this.vcsLevels.find((level) => level.id === asset.unitId)?.displayName || ""
			};

			this.selectedTagsName = asset.tags.map((tag) => tag.name);
			this.isEditAsset = true;
		});
	}

	public openImageEditor() {
		let metadata: ImageMetaData | undefined;
		if (this.addEditForm.metadata) {
			metadata = JSON.parse(this.addEditForm.metadata);
		}
		this.$refs.imageEditor.startEditing(metadata);
	}

	get ImageSubmitText() {
		return fmtMsg(this.assetListLocale.AssetListImageSumbitTxt);
	}

	get ImageCancelText() {
		return fmtMsg(this.assetListLocale.AssetListImageCancelTxt);
	}

	get imageSelectorType() {
		return IImageSelectorType.Asset;
	}

	get selectedPageImageId() {
		if (this.addEditForm.pageId) {
			return this.addEditForm.pageId;
		}

		return "";
	}

	get selectedPageContentId() {
		if (this.addEditForm.contentId) {
			return this.addEditForm.contentId;
		}

		return "";
	}

	onImageAttach(item: { name: string; type: string; pageId: string; source: string; metaData: ImageMetaData }) {
		const isDefaultMetaData = ({ x, y, height, width, rotate, scaleX, scaleY, annotations }: ImageMetaData) => {
			return (
				x === 0 &&
				y === 0 &&
				height === 0 &&
				width === 0 &&
				rotate === 0 &&
				scaleX === 1 &&
				scaleY === 1 &&
				!annotations?.length
			);
		};

		if (!isDefaultMetaData(item.metaData)) {
			this.addEditForm.metadata = JSON.stringify(item.metaData);
		}

		this.addEditForm.pageId = item.pageId;
		this.addEditForm.target = item.metaData.annotations?.length || 0;
	}

	get selectedVersionGroupId() {
		return this.versionGroupModule.selectedGsVersion;
	}

	get selectedVersionGroupsIds() {
		return [this.versionGroupModule.selectedGsVersion, this.versionGroupModule.selectedLsVersion];
	}

	unitOrComponentChange(data) {
		if (
			data.componentName != "" &&
			data.unitId != -1 &&
			(data.componentName != this.addEditForm.component || data.mediaId != this.addEditForm.mediaId)
		) {
			this.addEditForm.component = data.componentName;
			this.addEditForm.unitId = data.unitId;
			this.addEditForm.unitName =
				this.vcsLevels.find((vcsLevel) => vcsLevel.id === data.unitId)?.displayName || "";
			this.addEditForm.mediaId = data.mediaId;
		}
	}

	get tagNameCharacterLimitErrorText() {
		return fmtMsg(this.assetListLocale.AssetListDrawerTagNameLengthErrorText, {
			0: AssetListValidations.tagNameCharacterLimit
		});
	}

	get tagNameUniqueErrorText() {
		return fmtMsg(this.assetListLocale.AssetListDrawerTagNameUniqueErrorText);
	}
}
